// src/services/tigerWebSocket.js
class TigerWebSocketService {
  constructor() {
    this.ws = null;
    this.subscriptions = new Set();
    this.messageId = 1;
    this.callbacks = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 1;
    this.isConnected = false;
    this.reconnectInterval = 600000; // 60 seconds
  }

  connect() {
    return new Promise((resolve, reject) => {
      try {
        // Tiger Trade WebSocket endpoint (sandbox)
        const wsUrl = 'wss://openapi.tigerfintech.com/socket.io/?EIO=3&transport=websocket';
        this.ws = new WebSocket(wsUrl);

        this.ws.onopen = () => {
          console.log('WebSocket connected to Tiger Trade');
          this.reconnectAttempts = 1;
          this.authenticate();
          resolve();
        };

        this.ws.onmessage = (event) => {
          this.handleMessage(event.data);
        };

        this.ws.onclose = (event) => {
          console.log('WebSocket disconnected:', event.code, event.reason);
//          this.handleReconnection();
        };

        this.ws.onerror = (event) => {
          console.error('WebSocket error occured:', event);
//          this.handleReconnection();
        };

      } catch (error) {
        reject(error);
      }
    });
  }



  // async connect() {
  //   try {
  //     // First authenticate via REST API
  //     const authToken = await this.authenticate();
      
  //     // Then connect to WebSocket with auth token
  //     const wsUrl = `wss://openapi.tigerfintech.com/streaming?token=${authToken}`;
  //     this.ws = new WebSocket(wsUrl);

  //     this.setupWebSocketHandlers();
      
  //   } catch (error) {
  //     console.error('WebSocket connection failed:', error);
  //     this.scheduleReconnection();
  //   }
  // }  

  authenticate() {
    // Tiger Trade requires authentication via WebSocket
    const authMessage = {
      service: 'auth',
      version: '1.0',
      id: this.getMessageId(),
      data: {
        tiger_id: process.env.REACT_APP_TIGER_ID,
        account: process.env.REACT_APP_ACCOUNT,
        sign: this.generateSignature() // You'll need to implement signature generation
      }
    };
    this.send(JSON.stringify(authMessage));
  }

  // async authenticate() {
  //   try {
  //     // Use REST API to get authentication token first
  //     const response = await fetch('https://openapi.tigerfintech.com/auth/v1/token', {
  //       method: 'POST',
  //       headers: {
  //         'Content-Type': 'application/json',
  //         'X-API-KEY': process.env.REACT_APP_TIGER_API_KEY
  //       },
  //       body: JSON.stringify({
  //         tiger_id: process.env.REACT_APP_TIGER_ID,
  //         account: process.env.REACT_APP_ACCOUNT,
  //         signature: this.generateSignature()
  //       })
  //     });

  //     const data = await response.json();
  //     return data.token;
  //   } catch (error) {
  //     console.error('Authentication failed:', error);
  //     throw error;
  //   }
  // }

  setupWebSocketHandlers() {
    this.ws.onopen = () => {
      console.log('WebSocket connected successfully');
      this.isConnected = true;
      this.onConnected?.();
    };

    this.ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        this.handleMessage(message);
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };

    this.ws.onclose = (event) => {
      console.log('WebSocket disconnected:', event.code, event.reason);
      this.isConnected = false;
      this.scheduleReconnection();
    };

    this.ws.onerror = (event) => {
      console.error('WebSocket error:', event);
      this.isConnected = false;
    };
  }

  scheduleReconnection() {
    setTimeout(() => {
      console.log('Attempting to reconnect...');
      this.connect();
    }, this.reconnectInterval);
  }  

  subscribeToQuotes(symbols) {
    const subscribeMessage = {
      service: 'quote',
      version: '1.0',
      id: this.getMessageId(),
      data: {
        symbols: symbols,
        fields: ['last', 'volume', 'open', 'high', 'low', 'prev_close', 'timestamp']
      }
    };
    this.send(JSON.stringify(subscribeMessage));
  }

  subscribeToDepth(symbol) {
    const depthMessage = {
      service: 'depth',
      version: '1.0',
      id: this.getMessageId(),
      data: {
        symbol: symbol,
        level: 10 // Market depth levels
      }
    };
    this.send(JSON.stringify(depthMessage));
  }

  unsubscribeFromQuotes(symbols) {
    const unsubscribeMessage = {
      service: 'quote',
      version: '1.0',
      id: this.getMessageId(),
      data: {
        symbols: symbols,
        unsubscribe: true
      }
    };
    this.send(JSON.stringify(unsubscribeMessage));
  }

  send(message) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(message);
    }
  }

  handleMessage(data) {
    try {
      const message = JSON.parse(data);
      
      switch (message.service) {
        case 'auth':
          this.handleAuthResponse(message);
          break;
        case 'quote':
          this.handleQuoteUpdate(message);
          break;
        case 'depth':
          this.handleDepthUpdate(message);
          break;
        case 'kline':
          this.handleKlineUpdate(message);
          break;
        default:
          console.log('Unknown message type:', message);
      }
    } catch (error) {
      console.error('Error parsing WebSocket message:', error, data);
    }
  }

  handleAuthResponse(message) {
    if (message.code === 0) {
      console.log('Authentication successful');
      // Subscribe to initial symbols
      this.subscribeToQuotes(Array.from(this.subscriptions));
    } else {
      console.error('Authentication failed:', message);
    }
  }

  handleQuoteUpdate(message) {
    if (message.data && message.data.items) {
      message.data.items.forEach(quote => {
        const callback = this.callbacks.get(quote.symbol);
        if (callback) {
          callback(quote);
        }
      });
    }
  }

  handleDepthUpdate(message) {
    // Handle market depth updates
    console.log('Depth update:', message);
  }

  handleKlineUpdate(message) {
    // Handle candlestick data updates
    console.log('Kline update:', message);
  }

  onQuoteUpdate(symbol, callback) {
    this.callbacks.set(symbol, callback);
    this.subscriptions.add(symbol);
    
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.subscribeToQuotes([symbol]);
    }
  }

  offQuoteUpdate(symbol) {
    this.callbacks.delete(symbol);
    this.subscriptions.delete(symbol);
    
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.unsubscribeFromQuotes([symbol]);
    }
  }

  getMessageId() {
    return this.messageId++;
  }

  generateSignature() {
    // Implement Tiger Trade signature generation
    // This typically involves timestamp + secret key hashing
    return 'your-generated-signature';
  }

  handleReconnection() {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
      
      setTimeout(() => {
        console.log(`Reconnection attempt ${this.reconnectAttempts}`);
        this.connect();
      }, delay);
    }
  }


  disconnect() {

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.callbacks.clear();
  }
}

export const tigerWebSocketService = new TigerWebSocketService();